import { type ImageJob, operationSpec } from '@acasa/shared'
import { CompareSlider } from './compare-slider'
import { JobProgress } from './job-progress'
import { STATUS_LABELS } from './operations'

/**
 * One job, in full: status, progress, the before/after comparison and the
 * prompt that produced it.
 *
 * Shared by the queue and the history, so a result found weeks later shows
 * exactly what it showed the day it was made.
 */
export function JobCard({
  job,
  onRemove,
  /** ImmoNr of the object, shown only in the history where jobs are mixed. */
  estateLabel,
}: {
  job: ImageJob
  onRemove: (id: string) => void
  estateLabel?: string
}) {
  const spec = operationSpec(job.operation)
  const running = job.status === 'queued' || job.status === 'running'

  return (
    <article className="bg-surface border-line border">
      <header className="border-line flex items-baseline justify-between gap-3 border-b px-4 py-2.5">
        <span className="min-w-0">
          <span className="text-ink text-xs font-medium">{spec.label}</span>
          {job.style && <span className="text-muted ml-2 text-[11px]">{job.style}</span>}
          {estateLabel && (
            <span className="text-faint ml-2 font-mono text-[11px]">{estateLabel}</span>
          )}
          {job.prompt && (
            <span className="text-muted mt-0.5 block truncate text-[11px] italic">
              „{job.prompt}“
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span
            className={`text-[10px] tracking-wider uppercase ${
              job.status === 'failed'
                ? 'text-danger'
                : job.status === 'succeeded'
                  ? 'text-done'
                  : 'text-muted'
            }`}
          >
            {STATUS_LABELS[job.status]}
          </span>
          <button
            type="button"
            onClick={() => onRemove(job.id)}
            aria-label="Auftrag entfernen"
            className="text-faint hover:text-danger text-xs"
          >
            ✕
          </button>
        </span>
      </header>

      {running && <JobProgress job={job} />}

      {job.status === 'failed' && <p className="text-danger px-4 py-4 text-xs">{job.error}</p>}

      {job.status === 'succeeded' && job.resultUrl && (
        <>
          <CompareSlider
            // Left: the untouched picture from onOffice, at the same size the
            // model was given. Right: what came back.
            before={`${job.sourceUrl}@1600x1200`}
            after={job.resultUrl}
            alt={`${spec.label} — Vergleich`}
          />
          <footer className="border-line flex flex-wrap items-center gap-2 border-t px-4 py-2.5">
            {/*
              Not a placeholder that will be wired up later: writing to onOffice
              needs the `do` action, which actions.ts deliberately does not
              declare. Whoever specs this feature has to decide that first — so
              the button says why instead of implying it is coming.
            */}
            <button
              type="button"
              disabled
              title="Der Rückweg nach onOffice ist bewusst nicht gebaut"
              className="border-line text-faint cursor-not-allowed border px-3 py-1.5 text-[11px]"
            >
              In onOffice übernehmen
            </button>
            <span className="text-faint text-[11px]">
              Schreibzugriff auf onOffice ist bewusst nicht implementiert.
            </span>
            <a
              href={job.resultUrl}
              download={`${job.operation}-${job.sourceImageId}.png`}
              className="border-line text-muted hover:text-ink border px-3 py-1.5 text-[11px] transition-colors"
            >
              Ergebnis herunterladen
            </a>
            <a
              href={job.sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-muted hover:text-ink ml-auto text-[11px] underline underline-offset-2"
            >
              Original öffnen
            </a>
          </footer>

          {/* The prompt that produced this, kept with the result: tuning a
              prompt against a remembered result is guesswork. */}
          <details className="border-line border-t px-4 py-2">
            <summary className="text-faint hover:text-muted cursor-pointer text-[11px]">
              Verwendeter Prompt
            </summary>
            <pre className="text-muted mt-1.5 max-h-56 overflow-auto font-mono text-[10px] leading-relaxed whitespace-pre-wrap">
              {job.modelPrompt}
            </pre>
          </details>
        </>
      )}
    </article>
  )
}
